import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReduceCircleLine from '@/assets/images/icons/reduce-circle-line.react.svg?react';
import { BASE_NAME, toSearchStr } from '@/infrastructure/history';
import { isLinux } from '@/infrastructure/product-info';
import { DATE_TIME_FORMAT } from '@/infrastructure/utils/timestamp';
import { MatchMode } from '@/presentation/components/form/standards/types';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { hintChildren } from '@/presentation/components/hint-text/hintText';
import styles from '@/presentation/components/issue-track-icon/index.module.scss';
import { useStores } from '@/presentation/store';
import { CommonFieldKey, StandardCommonFieldKey } from '@/proto/field';
import { IFilter, TimeType } from '@/proto/metric';
import { genStandardToken } from '@/proto/token';

interface IProps {
  eventTime: string;
  reportTime: string;
  issueId?: string | null;
  launchId?: string | null;
  processLaunchId?: string | null;
  accountId?: string | null;
  qimei?: string | null;
}
export const IssueTrackIcon = (props: IProps) => {
  const { t } = useTranslation();
  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const { eventTime, reportTime, launchId, processLaunchId, accountId, qimei } = props;
  const { pathname } = useLocation();
  if ((!launchId && !processLaunchId && !accountId && !qimei) || pathname === '/track' || isLinux()) {
    return null;
  }
  const click = async () => {
    const eventTimeUnix = eventTime && moment(eventTime, DATE_TIME_FORMAT).isValid()
      ? moment(eventTime, DATE_TIME_FORMAT).unix() : moment().unix();
    const reportTimeUnix = reportTime && moment(reportTime, DATE_TIME_FORMAT).isValid()
      ? moment(reportTime, DATE_TIME_FORMAT).unix() : moment().unix();
    const minTime = Math.min(eventTimeUnix, reportTimeUnix);
    const maxTime = Math.max(eventTimeUnix, reportTimeUnix);
    const startTime = moment.unix(minTime).subtract(10, 'minutes');
    const endTime = moment.unix(maxTime).add(10, 'minutes');
    const filters: IFilter[] = [];
    const values: Record<string, any> = {};
    if (launchId) {
      filters.push({ name: CommonFieldKey.LaunchId, values: [launchId], operator: MatchMode.OR });
      values[StandardCommonFieldKey.LaunchId] = { values: [launchId], mode: MatchMode.OR };
    }
    if (processLaunchId) {
      filters.push({ name: CommonFieldKey.ProcessLaunchId, values: [processLaunchId], operator: MatchMode.OR });
      values[StandardCommonFieldKey.ProcessLaunchId] = { values: [processLaunchId], mode: MatchMode.OR };
    }
    if (accountId) {
      filters.push({ name: CommonFieldKey.AccountId, values: [accountId], operator: MatchMode.OR });
      values[StandardCommonFieldKey.AccountId] = { values: [accountId], mode: MatchMode.OR };
    }
    if (qimei) {
      filters.push({ name: CommonFieldKey.Qimei, values: [qimei], operator: MatchMode.OR });
      values[StandardCommonFieldKey.Qimei] = { values: [qimei], mode: MatchMode.OR };
    }
    filters.push(
      { name: 'event_time', values: [startTime.unix().toString()], operator: 'GE' },
      { name: 'event_time', values: [endTime.unix().toString()], operator: 'LE' },
    );
    const token = await genStandardToken({
      date_type: 'event_time',
      short_cut: 'custom',
      time_window: TimeType.DAY,
      time_window_type: TimeType.DAY,
      date_value: [startTime.unix(), endTime.unix()],
      standard_time_range: {
        date_type: 'event_time',
        time_window_type: TimeType.DAY,
        short_cut: 'custom',
        date_value: [
          startTime.format(DATE_TIME_FORMAT),
          endTime.format(DATE_TIME_FORMAT),
        ],
      },
      ...values,
      filters,
    });
    const str = toSearchStr({
      token,
    });
    const a = document.createElement('a');
    const url = `${location.origin}${BASE_NAME}/track?${str}`;
    a.href = url;
    a.target = '_blank';
    a.click();
  };
  return <FreeProductGuide guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}>
    <div style={isFree ? { color: '#d9d9d9' } : undefined} className={styles.icon} onClick={click}>
      {isFree ? <ReduceCircleLine /> : hintChildren(<ReduceCircleLine />, t('追踪'))}
    </div>
  </FreeProductGuide>;
};
